import { PowerBIEmbed } from "powerbi-client-react";
import { Embed, Page, Report, models } from "powerbi-client";
import { useEffect, useRef, useState } from "react";
import { useAppSelector } from "utils/hooks/storeHooks";
import { selectEmbedToken } from "store/session/selectors";
import { getEmbedToken } from "store/session/thunks";
import { useDispatch } from "react-redux";
import styles from "./PowerBIReport.module.scss";

type PowerBIReportParams = {
  page: number;
  values: any;
  setIsReportLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  setReportRef: React.Dispatch<React.SetStateAction<Report | undefined>>;
  setReportState: React.Dispatch<React.SetStateAction<string>>;
};

const PowerBIReport = ({
  page,
  values,
  setIsReportLoaded,
  setReportRef,
  setReportState,
}: PowerBIReportParams) => {
  const dispatch = useDispatch();
  const [report, setReport] = useState<Report>();
  const [pages, setPages] = useState<Page[]>();
  const embedToken = useAppSelector(selectEmbedToken);
  const embedUrl = `https://app.powerbi.com/reportEmbed?reportId=${values?.reportId}&groupId=d0aa1a69-69a2-4202-af6b-22af36ebdb1b&w=2&config=eyJjbHVzdGVyVXJsIjoiaHR0cHM6Ly9XQUJJLVVTLUNFTlRSQUwtQS1QUklNQVJZLXJlZGlyZWN0LmFuYWx5c2lzLndpbmRvd3MubmV0IiwiZW1iZWRGZWF0dXJlcyI6eyJtb2Rlcm5FbWJlZCI6dHJ1ZSwidXNhZ2VNZXRyaWNzVk5leHQiOnRydWV9fQ%3d%3d`;
  const filterArr: any[] = [];
  const [keyReport, setKeyReport] = useState(0);
  const ref = useRef({
    report: report,
  });

  useEffect(() => {
    if (report) {
      ref.current.report = report;
      setReportRef(report);
    }
  }, [report, setReportRef]);

  const getPageNames = async () => {
    if (values?.templateState)
      await ref.current.report?.bookmarksManager.applyState(
        values?.templateState
      );
    setPages(await ref.current.report?.getPages());
  };

  const captureReportState = async () => {
    const clickedReport = ref.current.report;
    if (clickedReport) {
      const capturedState = await clickedReport.bookmarksManager.capture();
      if (capturedState.state) setReportState(capturedState.state);
    } else {
      console.log("No report state available");
    }
  };

  enum TablesEnum {
    physician_table = 1, 
  }
  enum ColumnCardEnum {
    enumeration_type = 1,
    organization_type = 3
  }

  function getTableById(id: number): string | null {
    const enumVal = TablesEnum[id];
    if (enumVal !== undefined) {
      return enumVal;
    } else {
      return "physician_table";
    }
  }
  function getColumnCardById(id: number): string | null {
    const enumVal = ColumnCardEnum[id];
    if (enumVal !== undefined) {
      return enumVal;
    } else {
      return null;
    }
  }

  if (values?.checkboxes && values?.checkboxes.length > 0) {
    let table_name = "";
    let column_name = "";

    if ((values.providerId === 1 || values.providerId === 4) && values.cardId !== 2) {
      table_name = "physician_table";
      column_name = "taxonomy_code";

      filterArr.push({
        $schema: "http://powerbi.com/product/schema#basic",
        target: {
          table: table_name,
          column: column_name,
        },
        operator: "In",
        values: values?.checkboxes || "",
        filterType: models.FilterType.Basic,
      });

    }    

    if (values.providerId === 1 && values.cardId === 2) {
      let filteredCheckboxes = values.checkboxes.filter((checkbox: string) => checkbox.trim() !== 'Clinics'); // Exclude 'Clinics'
    
      if (filteredCheckboxes.length > 0) { // Ensure there are other checkboxes to process
        table_name = "physician_table";
        column_name = "payor";
    
        filterArr.push({
          $schema: "http://powerbi.com/product/schema#basic",
          target: {
            table: table_name,
            column: column_name,
          },
          operator: "In",
          values: filteredCheckboxes,
          filterType: models.FilterType.Basic,
        });
      }
    }
    // console.log("Values checkboxes: ", values.checkboxes);
    if (values.providerId === 1 && values.cardId === 2) {
      if (values.checkboxes.map((checkbox : string) => checkbox.trim()).includes('Clinics')) {
          let table_name = "physician_table";
          let column_name = "organization_type";
  
          filterArr.push({
              $schema: "http://powerbi.com/product/schema#basic",
              target: {
                  table: table_name,
                  column: column_name,
              },
              operator: "In",
              values: ['Clinics'],
              filterType: models.FilterType.Basic,
          });
      }
  }
  
  }

  if (values?.states) {
    const locationFilter = {
      $schema: "http://powerbi.com/product/schema#basic",
      target: {
        table: "physician_table",
        column: "state",
      },
      operator: "In",
      values: [values?.states] || "",
      filterType: models.FilterType.Basic,
    };
    filterArr.push(locationFilter);
  }
  if (values?.counties) {
    const locationFilter = {
      $schema: "http://powerbi.com/product/schema#basic",
      target: {
        table: "physician_table",
        column: "county",
      },
      operator: "In",
      values: [values?.counties] || "",
      filterType: models.FilterType.Basic,
    };
    filterArr.push(locationFilter);
  }
  if (values?.cities) {
    const locationFilter = {
      $schema: "http://powerbi.com/product/schema#basic",
      target: {
        table: "physician_table",
        column: "city",
      },
      operator: "In",
      values: [values?.cities] || "",
      filterType: models.FilterType.Basic,
    };
    filterArr.push(locationFilter);
  }
  if (values?.cardValue) {
    filterArr.push({
      $schema: "http://powerbi.com/product/schema#basic",
      target: {
        table: String(getTableById(values?.providerId)),
        column: String(getColumnCardById(values?.providerId)),
      },
      operator: "And",
      values: [values?.cardValue] || "",
      filterType: models.FilterType.Basic,
    });
  }
  if (values.genieFilters) {
    values.genieFilters.forEach((filters: any) => {
      let filter: any = {
        $schema: "http://powerbi.com/product/schema#basic",
        target: {
          table: filters.tableName,
          column: filters.columnName,
        },
        operator: "In",
        values: filters.values || "",
        filterType: models.FilterType.Basic,
      };

      switch (filters.columnName) {
        case "postal_code":
        case "language_description":
          filter = {
            $schema: "http://powerbi.com/product/schema#advanced",
            target: {
              table: "physician_table",
              column: filters.columnName,
            },
            logicalOperator: "And",
            conditions: [
              {
                operator: "Contains",
                value: filters.values[0] || "",
              },
            ],
            filterType: models.FilterType.Advanced,
          };
          break;
        default:
          break;
      }
      filterArr.push(filter);
    });
  }
  // console.log("Filters: ", filterArr);

  const dispatchEmbedToken = () => {
    dispatch(getEmbedToken(String(values.providerId)));
  };

  useEffect(dispatchEmbedToken, [dispatch, values.providerId]);

  const embedConfig = {
    tokenType: models.TokenType.Embed,
    pageName: pages ? pages[page].name : "ReportSection",
    type: "report",
    id: values?.reportId,
    embedUrl,
    accessToken: embedToken,
    settings: {
      navContentPaneEnabled: false,
      filterPaneEnabled: false,
      bookmarksPaneEnabled: false,
      layoutType: models.LayoutType.Custom,
      customLayout: {
        pageSize: {
          type: models.PageSizeType.Widescreen,
        },
        displayOption: models.DisplayOption.FitToWidth,
      },
    },
    filters: filterArr,
  };

  return (
    <PowerBIEmbed
      key={keyReport}
      cssClassName={styles.embed}
      embedConfig={embedConfig}
      eventHandlers={
        new Map([
          [
            "loaded",
            function () {
              // console.log("Report loaded");
              getPageNames();
            },
          ],
          [
            "rendered",
            function () {
              // console.log("Report rendered");
              if (ref.current.report)
                setTimeout(() => {
                  setIsReportLoaded(true);
                }, 0);
              captureReportState();
            },
          ],
          [
            "error",
            function (event) {
              console.log(event && event.detail);
              if (event && event.detail.message === "LoadReportFailed") {
                console.log("restarting");

                setKeyReport(keyReport + 1);
              }
            },
          ],
          [
            "visualClicked",
            function () {
              // console.log("Visual clicked");
              captureReportState();
            },
          ],
          ["pageChanged", (event) => console.log(event)],
        ])
      }
      getEmbeddedComponent={(embeddedReport: Embed) => {
        setReport(embeddedReport as Report);
      }}
    />
  );
};

export default PowerBIReport;
